import { Variants } from "framer-motion";

// Common animation variants
export const fadeIn: Variants = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  exit: { opacity: 0 }
};

export const slideIn: Variants = {
  initial: { opacity: 0, x: -20 },
  animate: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: -20 }
};

export const slideInFromRight: Variants = {
  initial: { opacity: 0, x: 20 },
  animate: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: 20 }
};

export const slideUp: Variants = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: 20 }
};

export const scaleIn: Variants = {
  initial: { opacity: 0, scale: 0.95 },
  animate: { opacity: 1, scale: 1 },
  exit: { opacity: 0, scale: 0.95 }
};

export const cardHover: Variants = {
  initial: { scale: 1 },
  hover: { 
    scale: 1.02,
    boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)"
  }
};

export const buttonHover: Variants = {
  initial: { scale: 1 },
  hover: { scale: 1.05 },
  tap: { scale: 0.98 }
};

export const listItemVariants: Variants = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -20 }
};

export const staggerContainer: Variants = {
  animate: {
    transition: {
      staggerChildren: 0.1
    }
  }
};

export const pageTransition: Variants = {
  initial: { opacity: 0, y: 20 },
  animate: { 
    opacity: 1, 
    y: 0,
    transition: {
      duration: 0.4,
      ease: "easeOut"
    }
  },
  exit: { 
    opacity: 0, 
    y: -20,
    transition: {
      duration: 0.2,
      ease: "easeIn"
    }
  }
};

// Common animation options
export const defaultTransition = {
  duration: 0.3,
  ease: "easeOut"
};

export const smoothTransition = {
  duration: 0.2,
  ease: [0.4, 0, 0.2, 1]
};

export const springTransition = {
  type: "spring",
  stiffness: 300,
  damping: 30
};

// Collapse/expand animations for sidebar and accordions - Using reliable scaleY
export const collapseVariants: Variants = {
  expanded: {
    scaleY: 1,
    opacity: 1,
    transition: {
      scaleY: {
        duration: 0.3,
        ease: [0.4, 0, 0.2, 1]
      },
      opacity: {
        duration: 0.25,
        ease: "easeOut",
        delay: 0.05
      }
    }
  },
  collapsed: {
    scaleY: 0,
    opacity: 0,
    transition: {
      scaleY: {
        duration: 0.3,
        ease: [0.4, 0, 0.2, 1]
      },
      opacity: {
        duration: 0.2,
        ease: "easeIn"
      }
    }
  }
};

// Stagger container for collapsible items
export const collapseStaggerContainer: Variants = {
  expanded: {
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.1
    }
  },
  collapsed: {
    transition: {
      staggerChildren: 0.05,
      staggerDirection: -1
    }
  }
};

// Individual collapsible item animation
export const collapseItem: Variants = {
  expanded: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.25,
      ease: "easeOut"
    }
  },
  collapsed: {
    opacity: 0,
    y: -10,
    transition: {
      duration: 0.2,
      ease: "easeIn"
    }
  }
};

// Chevron rotation animation
export const chevronVariants: Variants = {
  expanded: {
    rotate: 90,
    transition: {
      duration: 0.3,
      ease: [0.4, 0, 0.2, 1]
    }
  },
  collapsed: {
    rotate: 0,
    transition: {
      duration: 0.3,
      ease: [0.4, 0, 0.2, 1]
    }
  }
};

// Tab indicator sliding animation
export const tabIndicatorVariants: Variants = {
  initial: {
    opacity: 0,
    scale: 0.95
  },
  animate: {
    opacity: 1,
    scale: 1,
    transition: {
      duration: 0.3,
      ease: [0.4, 0, 0.2, 1]
    }
  }
};

// Tab content transition animation
export const tabContentVariants: Variants = {
  initial: {
    opacity: 0,
    y: 8,
    scale: 0.98
  },
  animate: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      duration: 0.4,
      ease: [0.4, 0, 0.2, 1],
      delay: 0.1
    }
  },
  exit: {
    opacity: 0,
    y: -8,
    scale: 0.98,
    transition: {
      duration: 0.2,
      ease: [0.4, 0, 0.2, 1]
    }
  }
};

// Toggle group sliding indicator
export const toggleIndicatorVariants: Variants = {
  initial: {
    opacity: 0,
    scale: 0.9
  },
  animate: {
    opacity: 1,
    scale: 1,
    transition: {
      duration: 0.25,
      ease: [0.4, 0, 0.2, 1]
    }
  }
};

// Tab/Toggle hover animation
export const tabHoverVariants: Variants = {
  initial: {
    scale: 1,
    backgroundColor: "transparent"
  },
  hover: {
    scale: 1.02,
    transition: {
      duration: 0.2,
      ease: "easeOut"
    }
  },
  tap: {
    scale: 0.98,
    transition: {
      duration: 0.1,
      ease: "easeInOut"
    }
  }
};

// Login page specific animations with smooth, organic timing optimized for 60fps
export const loginFadeIn: Variants = {
  initial: { 
    opacity: 0, 
    y: 8,
    scale: 0.96,
    rotateX: 2, // Force GPU layer
  },
  animate: { 
    opacity: 1, 
    y: 0,
    scale: 1,
    rotateX: 0,
    transition: {
      duration: 0.6,
      ease: [0.16, 1, 0.3, 1], // Optimized for 60fps smoothness
      opacity: { duration: 0.4 },
      y: { duration: 0.6, ease: [0.16, 1, 0.3, 1] },
      scale: { duration: 0.6, ease: [0.16, 1, 0.3, 1] },
      rotateX: { duration: 0.6, ease: [0.16, 1, 0.3, 1] }
    }
  },
  exit: { 
    opacity: 0, 
    y: -8,
    scale: 0.98,
    rotateX: -1,
    transition: {
      duration: 0.3,
      ease: [0.4, 0, 0.6, 1]
    }
  }
};

export const loginSlideUp: Variants = {
  initial: { 
    opacity: 0, 
    y: 24,
    scale: 0.95,
    rotateX: 3, // GPU acceleration hint
  },
  animate: { 
    opacity: 1, 
    y: 0,
    scale: 1,
    rotateX: 0,
    transition: {
      type: "spring",
      stiffness: 120, // Increased for smoother 60fps
      damping: 14,    // Reduced for less overdamping
      mass: 0.8,      // Lighter for quicker response
      velocity: 2     // Initial velocity for smoothness
    }
  },
  exit: { 
    opacity: 0, 
    y: -16,
    scale: 0.98,
    rotateX: -2,
    transition: {
      duration: 0.25,
      ease: [0.4, 0, 0.6, 1] // Smoother exit curve
    }
  }
};

export const loginSlideInLeft: Variants = {
  initial: { 
    opacity: 0, 
    x: -16,
    scale: 0.98,
    rotateY: 2, // GPU acceleration hint
  },
  animate: { 
    opacity: 1, 
    x: 0,
    scale: 1,
    rotateY: 0,
    transition: {
      duration: 0.5,
      ease: [0.16, 1, 0.3, 1], // 60fps optimized curve
      x: { type: "spring", stiffness: 100, damping: 15 },
      opacity: { duration: 0.3 },
      scale: { duration: 0.5, ease: [0.16, 1, 0.3, 1] },
      rotateY: { duration: 0.5, ease: [0.16, 1, 0.3, 1] }
    }
  },
  exit: { 
    opacity: 0, 
    x: -16,
    scale: 0.98,
    rotateY: 1,
    transition: {
      duration: 0.25,
      ease: [0.4, 0, 0.6, 1]
    }
  }
};

export const loginFormStagger: Variants = {
  initial: {},
  animate: {
    transition: {
      staggerChildren: 0.08, // Faster stagger for smoother feel
      delayChildren: 0.15,   // Reduced delay for quicker start
      when: "beforeChildren" // Ensure parent animates first
    }
  }
};

export const loginFormItem: Variants = {
  initial: { 
    opacity: 0, 
    y: 16,
    scale: 0.98,
    rotateX: 1, // Subtle GPU hint
  },
  animate: { 
    opacity: 1, 
    y: 0,
    scale: 1,
    rotateX: 0,
    transition: {
      type: "spring",
      stiffness: 150, // Higher stiffness for 60fps
      damping: 18,    // Balanced damping
      mass: 0.7,      // Light mass for responsiveness
      opacity: { duration: 0.3, ease: [0.16, 1, 0.3, 1] }
    }
  }
};

export const loginLogo: Variants = {
  initial: { 
    opacity: 0, 
    scale: 0.8,
    y: -12,
    rotateZ: 5, // GPU acceleration with subtle rotation
  },
  animate: { 
    opacity: 1, 
    scale: 1,
    y: 0,
    rotateZ: 0,
    transition: {
      type: "spring",
      stiffness: 140, // Higher for crisp movement
      damping: 12,    // Less damping for livelier feel
      mass: 0.6,      // Lighter mass
      velocity: 3,    // Initial velocity for smooth start
    }
  },
  exit: { 
    opacity: 0, 
    scale: 0.9,
    y: -8,
    rotateZ: -3,
    transition: {
      duration: 0.25,
      ease: [0.4, 0, 0.6, 1]
    }
  }
};

export const loginButton: Variants = {
  initial: { 
    opacity: 0, 
    y: 8,
    scale: 0.96,
    rotateX: 2, // GPU hint
  },
  animate: { 
    opacity: 1, 
    y: 0,
    scale: 1,
    rotateX: 0,
    transition: {
      type: "spring",
      stiffness: 160, // High stiffness for crisp response
      damping: 16,    
      mass: 0.6,
      opacity: { duration: 0.25, ease: [0.16, 1, 0.3, 1] }
    }
  },
  hover: {
    scale: 1.02,
    y: -1, // Subtle lift
    transition: {
      type: "spring",
      stiffness: 300, // Very responsive for interaction
      damping: 20
    }
  },
  tap: {
    scale: 0.98,
    y: 0,
    transition: {
      type: "spring",
      stiffness: 400, // Immediate response
      damping: 25
    }
  }
};

export const loginErrorMessage: Variants = {
  initial: { 
    opacity: 0, 
    y: -8,
    scale: 0.95,
    height: 0,
    rotateX: 3, // GPU acceleration
  },
  animate: { 
    opacity: 1, 
    y: 0,
    scale: 1,
    height: "auto",
    rotateX: 0,
    transition: {
      type: "spring",
      stiffness: 120,
      damping: 15,
      mass: 0.8,
      opacity: { duration: 0.2, ease: [0.16, 1, 0.3, 1] },
      height: { duration: 0.3, ease: [0.16, 1, 0.3, 1] }
    }
  },
  exit: { 
    opacity: 0, 
    y: -8,
    scale: 0.95,
    height: 0,
    rotateX: -2,
    transition: {
      duration: 0.2,
      ease: [0.4, 0, 0.6, 1]
    }
  }
};

// Stagger animation for tab/toggle groups
export const tabStaggerContainer: Variants = {
  initial: {
    opacity: 0
  },
  animate: {
    opacity: 1,
    transition: {
      staggerChildren: 0.08,
      delayChildren: 0.1,
      duration: 0.3
    }
  }
};

export const tabStaggerItem: Variants = {
  initial: {
    opacity: 0,
    y: 8,
    scale: 0.95
  },
  animate: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      duration: 0.4,
      ease: [0.4, 0, 0.2, 1]
    }
  }
};